import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MealTile from '../../../component/HealthComponent/MealTile';
import SearchInput from '../../../component/common/Input/SearchInput';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { getLocalStorageItem, errorToast, successToast } from '../../../utils/helper';
import { deleteRecipe, getRecipe } from '../../../api/healthAPI';
import { DeleteApi } from '../../../api/api';
import Pagination from '../../../component/common/Pagination/Pagination';
import { PER_PAGE_12 as PER_PAGE } from '../../../utils/constants';
import ConfirmPopup from '../../../component/common/modals/ConfirmPopup';

const Meals = () => {
  const navigate = useNavigate();
  const selectedTheme = JSON.parse(localStorage.getItem('selectedTheme'));
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));
  const ref2 = useRef(null);
  const calendarRef = useRef(null);
  const createNewRef = useRef(null);
  const [isOpen2, setIsOpen2] = useState(false);
  const [isCreateNewOpen, setIsCreateNewOpen] = useState(false);
  const [startDate, setStartDate] = useState();
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mealToDelete, setMealToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchMeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedPerPage, startDate]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setCurrentPage(1);
      fetchMeals();
    }, 400);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  const fetchMeals = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: selectedPerPage?.value || 12,
        search: searchTerm || undefined,
        createdDate: startDate || undefined
      };
      const response = await getRecipe(params);
      console.log('Meals response:', response);

      if (response?.data?.recipes) {
        setMeals(response.data.recipes);
        const pagination = response.data.pagination;
        setTotalCount(pagination?.total || response.data.recipes.length);
      } else {
        setMeals([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching meals:', error);
      setMeals([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (e, meal) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === meal._id ? null : meal._id);
  };

  const handleMealClick = (meal) => {
    navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/recipe/new?id=${meal._id}`, {
      state: { _id: meal._id, action: 'edit' }
    });
    setOpenMenuId(null);
  };

  const handleView = (meal) => {
    navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/recipe/${meal._id}`, {
      state: { _id: meal._id, action: 'view' }
    });
    setOpenMenuId(null);
  };

  const handleEdit = (meal) => {
    navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/recipe/new?id=${meal._id}`, {
      state: { _id: meal._id, action: 'edit' }
    });
    setOpenMenuId(null);
  };

  const handleDelete = (meal) => {
    setMealToDelete(meal);
    setShowDeleteConfirm(true);
    setOpenMenuId(null);
  };

  const confirmDelete = async () => {
    if (!mealToDelete) return;

    try {
      setIsDeleting(true);

      const response = await deleteRecipe(mealToDelete._id);
      if (response?.data?.meta?.code === 1) {
        successToast('Recipe deleted successfully');
        fetchMeals();
      } else {
        throw new Error(response?.data?.meta?.message || 'Failed to delete recipe');
      }
      console.log('Delete meal:', mealToDelete._id);
      successToast('Delete functionality will be implemented soon');
      setShowDeleteConfirm(false);
      setMealToDelete(null);
    } catch (error) {
      console.error('Error deleting meal:', error);
      errorToast(error.message || 'Failed to delete recipe');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setMealToDelete(null);
  };

  const handleChangeC = (e) => {
    const date = new Date(e);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    setStartDate(formattedDate);
    setIsOpen2(false);
  };

  return (
    <div className="relative lg:mb-[45px]">
      {/*search options, Date filter & Add Company are here */}
      <div className="sm:flex flex-col md:flex-row justify-between items-center gap-3">
        {/* Left-aligned search input */}
        <div className="flex items-center justify-start sm:justify-center w-full sm:w-auto">
          <SearchInput
            id="searchKey"
            name="searchKey"
            type="text"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search"
            className="w-full sm:w-auto px-3 py-2"
          />
        </div>

        {/* Right-aligned buttons */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-center mt-2 sm:mt-0 space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
          <div ref={ref2} className="relative inline-block text-left w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setIsOpen2(!isOpen2)}
              className="flex justify-between items-center w-full sm:w-auto px-5 py-2 text-xs sm:text-base font-medium border dark:border-text-shoorah-darkBg border-[#0B1D2E] rounded-full dark:text-shoorah-darkBg dark:border-shoorah-darkBg"
            >
              <span>
                {startDate ? `${new Date(startDate).toLocaleDateString()}` : 'Date Created'}
              </span>
              {selectedTheme ? (
                <>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.2802 9.9668L12.9335 14.3135C12.4202 14.8268 11.5802 14.8268 11.0669 14.3135L6.72021 9.9668"
                      stroke="#F3F4F6"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              ) : (
                <>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.2802 9.9668L12.9335 14.3135C12.4202 14.8268 11.5802 14.8268 11.0669 14.3135L6.72021 9.9668"
                      stroke="#0B1D2E"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              )}
            </button>
            {isOpen2 && (
              <div
                className="absolute sm:-top-2 top-0 md:-left-40 sm:-left-10 left-0 w-full sm:w-auto transform scale-90 sm:scale-100 z-10 mt-1 p-2 bg-white shadow-lg ring-1 ring-[#E7EAE8] dark:bg-shoorah-darkBgColor rounded-md"
                ref={calendarRef}
              >
                <div className="bg-white dark:bg-shoorah-darkBgColor text-[14px]">
                  <Calendar
                    className={'calendar text-sm sm:text-lg'}
                    onChange={handleChangeC}
                    nextLabel={
                      <button className="custom-next-button flex justify-center items-center">
                        <ChevronRightIcon className="w-8 h-8" />
                      </button>
                    }
                    maxDate={new Date()}
                    prevLabel={
                      <button className="custom-prev-button flex justify-center items-center">
                        <ChevronLeftIcon className="w-8 h-8" />
                      </button>
                    }
                    date={startDate ? new Date(startDate) : new Date()}
                    navigationLabel={({ date }) => (
                      <button
                        style={{ cursor: 'default', pointerEvents: 'none' }}
                        className="text-base"
                      >
                        {date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                      </button>
                    )}
                  />
                </div>
              </div>
            )}
          </div>
          <div ref={createNewRef} className="relative inline-block text-left w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setIsCreateNewOpen(!isCreateNewOpen)}
              className="btn-primary w-full sm:w-auto"
            >
              Create New{' '}
              {selectedTheme ? (
                <>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.2802 9.9668L12.9335 14.3135C12.4202 14.8268 11.5802 14.8268 11.0669 14.3135L6.72021 9.9668"
                      stroke="#F3F4F6"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              ) : (
                <>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.2802 9.9668L12.9335 14.3135C12.4202 14.8268 11.5802 14.8268 11.0669 14.3135L6.72021 9.9668"
                      stroke="#0B1D2E"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              )}
            </button>
            {isCreateNewOpen && (
              <div className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white dark:bg-shoorah-darkBgColor shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <Link
                    to={`${userData?.slug ? `/${userData.slug}` : ''}/health/recipe/new`}
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-shoorah-darkBgTabColor"
                    onClick={() => setIsCreateNewOpen(false)}
                  >
                    New Recipe
                  </Link>
                  <Link
                    to={`${userData?.slug ? `/${userData.slug}` : ''}/health/equipments`}
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-shoorah-darkBgTabColor"
                    onClick={() => setIsCreateNewOpen(false)}
                  >
                    Equipment
                  </Link>
                  <Link
                    to={`${userData?.slug ? `/${userData.slug}` : ''}/health/health-categories`}
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-shoorah-darkBgTabColor"
                    onClick={() => setIsCreateNewOpen(false)}
                  >
                    Workout Categories
                  </Link>
                  <Link
                    to={`${userData?.slug ? `/${userData.slug}` : ''}/health/exercise/new`}
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-shoorah-darkBgTabColor"
                    onClick={() => setIsCreateNewOpen(false)}
                  >
                    New Exercise
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="bg-white mt-4 rounded-lg p-6">
        {/* Header with View All button */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Meals</h2>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        )}

        {/* Meal Grid */}
        {!loading && meals.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {meals.map((meal) => (
              <div key={meal._id} className="relative">
                <MealTile
                  image={meal.image || meal.image_url || '/assets/meals/default.jpg'}
                  mealName={meal.meal_name || meal.title}
                  status={meal.status || 'draft'}
                  calories={meal.calories || meal.kcal || null}
                  badgeText={meal.category || meal.cuisine || 'Meal'}
                  onClick={() => handleMealClick(meal)}
                  onMenuClick={(e) => handleMenuClick(e, meal)}
                />

                {/* Dropdown Menu */}
                {openMenuId === meal._id && (
                  <div className="absolute top-12 right-2 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px]">
                    <button
                      onClick={() => handleView(meal)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      View
                    </button>
                    <button
                      onClick={() => handleEdit(meal)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(meal)}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && meals.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No meals</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new meal.</p>
          </div>
        )}

        {/* Pagination */}
        <div className="mt-6">
          <div className="flex items-center justify-end gap-4 mb-4">
            <select
              value={selectedPerPage.value}
              onChange={(e) => {
                const val = Number(e.target.value);
                const option = PER_PAGE.find((p) => p.value === val) || PER_PAGE[0];
                setSelectedPerPage(option);
                setCurrentPage(1);
              }}
              className="border rounded-md px-3 py-2 text-sm"
            >
              {PER_PAGE.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={selectedPerPage?.value || 12}
            onPageChange={(page) => setCurrentPage(page)}
            disabled={loading}
          />
        </div>
      </div>

      {/* Delete Confirmation Popup */}
      <ConfirmPopup
        open={showDeleteConfirm}
        setOpen={setShowDeleteConfirm}
        setAccepted={confirmDelete}
        handleNo={handleDeleteCancel}
        message={`Are you sure you want to delete "${
          mealToDelete?.title || mealToDelete?.meal_name
        }"? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Meals;
